/**
 * react-query 查询封装。
 *
 * 只封装**读**。写操作在各自的页面里用 `useMutation` 直接调 `api` —— 因为
 * 每个写操作的后续动作不同（有的要提示冲突、有的要刷新三处缓存），放在一处
 * 反而制造一个「什么都做一点」的抽象。
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "./client.js";
import type { EntryView } from "./types.js";

export type EntryFilter = "all" | "theirs" | "mine";

export function useEntries(filter: EntryFilter) {
  return useQuery({
    queryKey: ["entries", filter],
    queryFn: () => api.listEntries(filter),
  });
}

export function useEntry(id: string | undefined) {
  return useQuery({
    queryKey: ["entry", id],
    queryFn: () => api.getEntry(id as string),
    enabled: id !== undefined,
  });
}

export function useReplies(entryId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["replies", entryId],
    queryFn: () => api.listReplies(entryId as string),
    enabled: entryId !== undefined && enabled,
  });
}

export function useEntryAttachments(
  entryId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["attachments", entryId],
    queryFn: () => api.entryAttachments(entryId as string),
    enabled: entryId !== undefined && enabled,
    retry: false,
  });
}

export function usePairing() {
  return useQuery({
    queryKey: ["pairing"],
    queryFn: api.pairing,
  });
}

export function useAnniversaries() {
  return useQuery({
    queryKey: ["anniversaries"],
    queryFn: api.listAnniversaries,
  });
}

export function useAnniversaryEntries(id: string | undefined) {
  return useQuery({
    queryKey: ["anniversary-entries", id],
    queryFn: () => api.anniversaryEntries(id as string),
    enabled: id !== undefined,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: api.settings,
  });
}

export function useSessions() {
  return useQuery({
    queryKey: ["sessions"],
    queryFn: api.sessions,
  });
}

export function useExportStatus() {
  return useQuery({
    queryKey: ["export-status"],
    queryFn: api.exportStatus,
  });
}

/**
 * 首页「TA 今天分享了一篇日记」。
 *
 * 只由**共享**日记推导（spec §4.3）—— 后端 `/api/entries?filter=theirs`
 * 本来就只返回对方共享的篇目，所以这里直接用它，不另开统计接口。
 * 本人新增私密日记不会改变这个查询的结果。
 */
export function useTodaySharedByPartner(
  entries: EntryView[] | undefined,
  now: Date = new Date(),
): boolean {
  if (entries === undefined) return false;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return entries.some((e) => {
    const t = new Date(e.writtenAt).getTime();
    return t >= start.getTime() && t < end.getTime();
  });
}
